/* eslint-disable max-lines */
import { Component, OnInit, Inject, AfterViewInit, OnDestroy } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { combineLatest, Observable, Subscription, zip, of, iif } from 'rxjs';
import { map, take, tap, filter, switchMap, skipWhile, distinctUntilChanged } from 'rxjs/operators';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { BookingFacade } from '@booking/booking.facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AppFacade } from '@app/app.facade';
import {
  AirValidationBySectorResponse,
  AirValidationResponse,
  AirValidationResponseError
} from '@app/entities/air-validation.response';
import { User } from '@app/modules/core/interfaces/user';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { PaxFacade } from '@pax/pax.facade';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import {
  bookingDetailRoute, BOOK_SEARCH_ID_PARAM, BOOK_SEARCH_AIR_ID_PARAM
} from '@booking/constants/booking-routes.constants';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { UpsellBookingProps } from '@upsell/entities/upsell-booking-params';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { userHistoryLast } from '@user-history/constants/history-routes.constants';
import { LoginType } from '@app/entities/login-type';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { ButtonType } from '@design-system/components/molecules/button/entities/button.types';
import { getActivitiesBookingFareParam } from '@activities/activities-results/mappers/activities-routing.mapper';
import { ActivitiesValidateResponse } from '@activities/entities/activities-booking';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { NavigationService } from '@app/services/navigation.service';

@Component({
  selector: 'app-booking',
  templateUrl: './booking.container.html',
  styleUrls: ['./booking.container.sass'],
  standalone: false
})

export class BookingContainer implements OnInit, AfterViewInit, OnDestroy {

  public showBookingDetail: boolean = false;
  public afterViewInit: boolean = false;
  public loggedIn: boolean;
  public supportImg: string = 'ilustra-soporte.jpg';
  public openBillingModal: boolean = false;
  public companyCode: string;
  public scrollIsBlocked: boolean = false;
  public travelType: TravelBookingType;

  public readonly secondaryButtonType = ButtonType.Secondary;

  public readonly icons = Icon;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appFacade: AppFacade,
    private facade: BookingFacade,
    private paxFacade: PaxFacade,
    private baseSearchbarfacade: BaseSearchBarFacade,
    private viewportScroller: ViewportScroller,
    private bookingAmplitudeService: BookingAmplitudeService,
    private domManipulateService: DomManipulateService,
    private headerService: HeaderService,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly navigationService: NavigationService,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.viewportScroller.scrollToPosition([0, 0]);
      if (this.currentRoute.endsWith(bookingDetailRoute)) {
        this.appFacade.historyReset();
        this.router.navigate([this.currentRoute.replace(`/${bookingDetailRoute}`, '')]);
      }
      this.initGeneralBooking();
    }
  }

  ngAfterViewInit(): void {
    if (this.loggedIn) {
      this.subscriptions.push(
        this.bookingAmplitudeService.
          registerViewBookingScreenAmplitudeEvent(this.travelType, this.navigationService.params.searchId));
      this.subscriptions.push(
        this.isChurningExceded$.subscribe(isChurningExceded => {
          if (isChurningExceded) this.domManipulateService.setBodyScroll(false);
        }));
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach(s => s?.unsubscribe());
    }
    this.facade.resetValidators();
    this.facade.setBookingType(null);
  }

  get airValidation$(): Observable<AirValidationResponse> {
    return this.facade.airValidation$;
  }

  get airValidationBySector$(): Observable<AirValidationBySectorResponse> {
    return this.facade.airValidationBySector$;
  }

  get validationError$(): Observable<AirValidationResponseError> {
    return this.facade.validationError$;
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }

  get user$(): Observable<User> {
    return this.facade.user$;
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get isOneWayTripType$(): Observable<boolean> {
    return combineLatest([this.airValidation$, this.airValidationBySector$]).pipe(
      map(([airValidation, airValidationBySector]) => {
        return this.isBySector
          ? this.validateOneWayBySector(airValidationBySector)
          : airValidation?.TripOption?.Sectors?.length === 1
      }
      )
    );
  }

  get hotelValidation$(): Observable<HotelValidationResponse> {
    return this.facade.hotelValidation$;
  }

  get autoValidation$(): Observable<VehicleValidateResponse> {
    return this.facade.autoValidation$;
  }

  get activitiesValidation$(): Observable<ActivitiesValidateResponse> {
    return this.facade.activitiesValidation$;
  }

  get isLoadingBookingBody$(): Observable<boolean> {
    return combineLatest([
      this.facade.isLoadingValidation$,
      this.paxFacade.isLoadingTravelers$
    ]).pipe(
      map(([isLoadingValidation, isLoadingTravelers]) =>
        isLoadingValidation || isLoadingTravelers)
    );
  }

  get showBookingHeader$(): Observable<boolean> {
    return combineLatest([this.isMobile$, this.isLoadingBookingBody$])
      .pipe(map(([isMobile, isLoadingBookingBody]) => isMobile ? !isLoadingBookingBody : true));
  }

  get isChurningExceded$(): Observable<boolean> {
    return this.facade.isChurningExceded$;
  }

  get travelBookingType(): typeof TravelBookingType {
    return TravelBookingType;
  }

  private get isBySector(): boolean {
    return this.travelType === TravelBookingType.FLIGHTS_BY_SECTOR;
  }

  private get currentRoute(): string {
    return this.router.url.split('?')[0];
  }

  public goBack(): void {
    this.navigationService.navigateToBack();
  }

  public toggleBookingDetail(): void {
    this.showBookingDetail = !this.showBookingDetail;
  }

  public onlyCloseChurningExceded(): void {
    this.facade.bookingChurningExceded(false);
    this.domManipulateService.setBodyScroll(true);
  }

  public closeAndBackChurningExceded(user: User, back: boolean = false): void {
    if (back) {
      this.appFacade.historyPop(); this.appFacade.navigateToLastRoute();
    } else {
      this.appFacade.historyReset(); this.router.navigate(['user-history', userHistoryLast]);
    }
    this.onlyCloseChurningExceded();
  }

  private validateOneWayBySector(airValidationBySector: AirValidationBySectorResponse): boolean {
    const sectors = airValidationBySector?.AirValidateResponse || [];
    const numSectors = sectors.length === 1 ? sectors[0]?.TripOption?.Sectors?.length || 0 : sectors.length;
    return numSectors === 1;
  }

  private initGeneralBooking(): void {
    this.subscriptions.push(
      combineLatest(
        [this.route.paramMap.pipe(
          switchMap(params => iif(
            () => !!params.get(UpsellBookingProps.upsellId) && !!params.get(UpsellBookingProps.upsellOptionId),
            of(params).pipe(
              distinctUntilChanged((prev, curr) =>
                prev.get(UpsellBookingProps.upsellId) === curr.get(UpsellBookingProps.upsellId) &&
                prev.get(UpsellBookingProps.upsellOptionId) === curr.get(UpsellBookingProps.upsellOptionId))
            ),
            of(params).pipe(take(1))
          ))
        ),
        this.isLoggedIn$,
        this.hotelValidation$.pipe(take(1)),
        this.user$.pipe(take(1))]
      ).pipe(
        map(([params, isloggedIn, hotelValidation, user]) => ({
          loggedIn: !!isloggedIn && !!user?.id,
          travelType: params.get('travelType') ? params.get('travelType') as TravelBookingType : null,
          params, hotelValidation, user
        })),
        tap(({ travelType, loggedIn }) => {
          this.loggedIn = loggedIn;
          if (!loggedIn) {
            this.headerService.forceUserLogin(LoginType.Booking, this.router.url);
          }
          if (loggedIn && isNullOrUndefined(travelType) || !Object.values(TravelBookingType)?.includes(travelType)) {
            setTimeout(() => this.goBack(), TIME_TO_SLIDE);
          }
        }
        ),
        filter(({ loggedIn, travelType }) => !!loggedIn && !!travelType &&
          !!Object.values(TravelBookingType)?.includes(travelType)),
        tap(({ travelType }) => {
          this.travelType = travelType;
          this.facade.setBookingType(this.travelType);
        }),
        map(data => ({
          ...data, isBuses: this.travelType === TravelBookingType.BUSES
        })),
        tap(() => this.facade.fetchCountries()),
        switchMap(({ hotelValidation, user, params, isBuses }) => zip(
          of({ hotelValidation, user, params, isBuses }),
          this.facade.countries$.pipe(skipWhile(countries => !countries || countries.length === 0))
        )),
        tap(([{ hotelValidation, user, params, isBuses }]) => {
          this.fetchValidationByProduct(hotelValidation, user, params, isBuses);
        })
      ).subscribe());
  }

  private fetchValidationByProduct(
    hotelValidation: HotelValidationResponse,
    user: User,
    params: ParamMap,
    isBuses: boolean): void {
    switch (this.travelType) {
      case TravelBookingType.BUSES:
      case TravelBookingType.FLIGHTS:
        if (isBuses) {
          this.baseSearchbarfacade.loadSearchData(SearchBy.AUTOS);
        }
        this.facade.fetchAirValidation({
          SearchId: params.get(BOOK_SEARCH_ID_PARAM),
          OptionId: params.get('optionId'), UpsellId: params.get(UpsellBookingProps.upsellId),
          UpsellOptionId: Number(params.get(UpsellBookingProps.upsellOptionId)) || undefined
        }, user, isBuses);
        break;
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        this.facade.fetchAirValidationBySector({
          SearchId: params.get(BOOK_SEARCH_ID_PARAM),
          AirValidateBySector: params.get('optionId').split('-').map(Number)
        }, user);
        break;
      case TravelBookingType.AUTOS:
        this.baseSearchbarfacade.loadSearchData(SearchBy.AUTOS);
        this.companyCode = params.get('companyCode');
        this.facade.fetchAutoValidation({
          DetailId: params.get('vehicleDetailId'), 
          IsPayNow: true, RateId: Number(params.get('rateId')),
        }, user);
        break;
      case TravelBookingType.PACKAGE_AIR:
        const upsell = params.get(UpsellBookingProps.upsellId);
        const upsellOption = params.get(UpsellBookingProps.upsellOptionId);
        const UpsellId = upsell?.toLowerCase() === 'null' ? undefined : upsell;
        const UpsellOptionId = upsellOption?.toLowerCase() === 'null' ? undefined : upsellOption;
        this.facade.fetchPackageValidation({
          RateIdHotel: params.get('rateIdHotel'),
          SearchId: params.get(BOOK_SEARCH_AIR_ID_PARAM),
          OptionIdHotel: params.get('optionIdHotel'),
          OptionIdAir: params.get('optionIdAir'),
          UpsellId,
          UpsellOptionId
        }, user);
        break;
      case TravelBookingType.HOTELS:
        if (isNullOrUndefined(hotelValidation) || isEmpty(hotelValidation)) {
          this.facade.fetchHotelValidation({
            SearchId: params.get(BOOK_SEARCH_ID_PARAM),
            OptionId: Number(params.get('optionId')),
            RateId: params.get('rateId')
          }, user, JSON.parse(params.get('byHoursData')));
        }
        break;
      case TravelBookingType.ACTIVITIES:
        const fares = getActivitiesBookingFareParam(params.get('rateId'), ['PaxType']);
        this.facade.fetchActivitiesValidate({
          SearchId: params.get(BOOK_SEARCH_ID_PARAM),
          OptionId: Number(params.get('optionId')),
          Products: fares
        }, user);
        break;
    }
  }

}
